import {
  AUTH_ACTIVITY_THROTTLE_MS,
  AUTH_IDLE_TIMEOUT_MS,
  expireAuthSession,
  readAuthSessionCookie,
  writeAuthSessionCookie,
} from "@/lib/auth/session";
import {
  clearOperationalAlertsForOperation,
  publishOperationalAlert,
} from "@/lib/operational-alert-events";
import { notifySystemError } from "@/lib/system-errors";
import type { AuthSession, RefreshTokenResponse } from "@/types/auth";
import type { OperationalAlertAction } from "@/types/operational-alert";
import {
  classifyFailure,
  isAbortFailure,
  isNetworkFailure,
} from "./failure-classification";

export type ApiErrorDetails = string | Record<string, unknown> | null;

const ACCESS_TOKEN_REFRESH_WINDOW_MS = 60 * 1000;
let refreshPromise: Promise<AuthSession> | null = null;

export type OperationalAlertOverride = {
  title?: string;
  description?: string;
};

export type ApiRequestFeedback = {
  operationKey?: string;
  operationalAlertOverride?: OperationalAlertOverride;
  operationalAlertAction?: OperationalAlertAction;
  suppressOperationalAlert?: boolean;
  treatTechnicalFailuresAsOperational?: boolean;
};

export type ApiRequestInit = RequestInit & ApiRequestFeedback;

function publishClassifiedOperationalFailure(
  failure: unknown,
  feedback: ApiRequestFeedback,
) {
  const classification = classifyFailure(failure);
  const isTechnicalFailure = classification.kind === "technical";
  if (
    (classification.kind !== "operational" &&
      !(isTechnicalFailure && feedback.treatTechnicalFailuresAsOperational)) ||
    feedback.suppressOperationalAlert ||
    !feedback.operationKey
  ) {
    return;
  }

  publishOperationalAlert({
    operationKey: feedback.operationKey,
    severity: "error",
    title:
      feedback.operationalAlertOverride?.title ??
      (classification.kind === "operational"
        ? classification.title
        : "Não foi possível concluir a operação"),
    description:
      feedback.operationalAlertOverride?.description ??
      (classification.kind === "operational"
        ? classification.description
        : "O serviço está temporariamente indisponível. Tente novamente."),
    ...(feedback.operationalAlertAction
      ? { action: feedback.operationalAlertAction }
      : {}),
  });
}

async function fetchApi(
  path: string,
  init: RequestInit,
  feedback: ApiRequestFeedback,
) {
  try {
    const response = await fetch(buildApiUrl(path), {
      ...init,
      cache: "no-store",
    });

    if (response.status >= 500 && response.status <= 599) {
      throw new ApiError(
        "O serviço está temporariamente indisponível.",
        response.status,
      );
    }

    return response;
  } catch (cause) {
    if (isAbortFailure(cause)) {
      throw cause;
    }

    if (
      (cause instanceof ApiError && cause.status >= 500) ||
      isNetworkFailure(cause)
    ) {
      if (feedback.treatTechnicalFailuresAsOperational) {
        const operationalError =
          cause instanceof ApiError
            ? cause
            : new ApiError("Não foi possível conectar ao servidor.", 0);
        publishClassifiedOperationalFailure(operationalError, feedback);
        throw operationalError;
      }

      notifySystemError();
      if (cause instanceof ApiError) {
        throw cause;
      }
      throw new ApiError("Não foi possível conectar ao servidor.", 0);
    }

    publishClassifiedOperationalFailure(cause, feedback);
    throw cause instanceof ApiError
      ? cause
      : new ApiError("Não foi possível concluir a operação.", -1);
  }
}

export class ApiError extends Error {
  status: number;
  details: ApiErrorDetails;

  constructor(message: string, status: number, details: ApiErrorDetails = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export function getApiBaseUrl() {
  return process.env.NEXT_PUBLIC_API_URL?.replace(/\/+$/, "") ?? "http://localhost:8000";
}

export function buildApiUrl(path: string) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${getApiBaseUrl()}${normalizedPath}`;
}

async function parseResponseBody(response: Response) {
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get("content-type") ?? "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  return text || null;
}

function createResponseParsingError() {
  return new ApiError(
    "O servidor retornou uma resposta que não pôde ser processada.",
    -1,
  );
}

function feedbackForParsingFailure(
  feedback: ApiRequestFeedback,
  method: string | undefined,
): ApiRequestFeedback {
  const normalizedMethod = (method ?? "GET").toUpperCase();
  const canSafelyRepeat = normalizedMethod === "GET" || normalizedMethod === "HEAD";

  return canSafelyRepeat
    ? feedback
    : { ...feedback, operationalAlertAction: undefined };
}

function extractErrorMessage(details: ApiErrorDetails) {
  if (typeof details === "string" && details.trim()) {
    return details;
  }

  if (details && typeof details === "object") {
    const record = details as Record<string, unknown>;

    if (typeof record.detail === "string" && record.detail.trim()) {
      return record.detail;
    }

    if (typeof record.message === "string" && record.message.trim()) {
      return record.message;
    }

    const nonFieldErrors = record.non_field_errors;
    if (Array.isArray(nonFieldErrors) && typeof nonFieldErrors[0] === "string") {
      return nonFieldErrors[0];
    }
  }

  return "Não foi possível concluir a solicitação.";
}

type RequestJsonInit = ApiRequestInit & {
  accessToken?: string | null;
  contentType?: string | null;
};

export async function requestJson<TResponse>(
  path: string,
  init: RequestJsonInit = {},
): Promise<TResponse> {
  const {
    accessToken,
    contentType = "application/json",
    operationKey,
    operationalAlertOverride,
    operationalAlertAction,
    suppressOperationalAlert,
    treatTechnicalFailuresAsOperational,
    headers,
    ...requestInit
  } = init;
  const feedback = {
    operationKey,
    operationalAlertOverride,
    operationalAlertAction,
    suppressOperationalAlert,
    treatTechnicalFailuresAsOperational,
  };
  const requestHeaders = new Headers(headers);
  if (contentType && !requestHeaders.has("Content-Type")) {
    requestHeaders.set("Content-Type", contentType);
  }
  if (accessToken) {
    requestHeaders.set("Authorization", `Bearer ${accessToken}`);
  }

  const response = await fetchApi(
    path,
    {
      ...requestInit,
      headers: requestHeaders,
    },
    feedback,
  );

  if (!response.ok) {
    const details = (await parseResponseBody(response).catch(
      () => null,
    )) as ApiErrorDetails;
    const apiError = new ApiError(
      extractErrorMessage(details),
      response.status,
      details,
    );
    publishClassifiedOperationalFailure(apiError, feedback);
    throw apiError;
  }

  let parsedBody: unknown;
  try {
    parsedBody = await parseResponseBody(response);
  } catch {
    const parsingError = createResponseParsingError();
    publishClassifiedOperationalFailure(
      parsingError,
      feedbackForParsingFailure(feedback, requestInit.method),
    );
    throw parsingError;
  }

  if (operationKey) {
    clearOperationalAlertsForOperation(operationKey);
  }

  return parsedBody as TResponse;
}

function getTokenExpiry(token: string) {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decodedPayload = atob(
      normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, "="),
    );
    const parsedPayload = JSON.parse(decodedPayload) as { exp?: unknown };

    return typeof parsedPayload.exp === "number" ? parsedPayload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function refreshAccessToken(session: AuthSession) {
  if (!refreshPromise) {
    refreshPromise = requestJson<RefreshTokenResponse>("/api/token/refresh/", {
      method: "POST",
      body: JSON.stringify({ refresh: session.refreshToken }),
    })
      .then((tokens) => {
        const nextSession: AuthSession = {
          ...session,
          accessToken: tokens.access,
          refreshToken: tokens.refresh ?? session.refreshToken,
          passwordExpired: tokens.password_expired,
          mustChangePassword: tokens.must_change_password,
          lastActivityAt: Date.now(),
        };
        writeAuthSessionCookie(nextSession);
        return nextSession;
      })
      .catch((cause) => {
        if (cause instanceof ApiError && cause.status === 401) {
          expireAuthSession();
          throw new ApiError("Sua sessão expirou. Faça login novamente.", 401, cause.details);
        }
        throw cause;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

async function getActiveSession(forceRefresh = false) {
  const session = readAuthSessionCookie();
  if (!session) {
    return null;
  }

  const now = Date.now();
  const lastActivityAt = session.lastActivityAt ?? now;
  if (now - lastActivityAt >= AUTH_IDLE_TIMEOUT_MS) {
    expireAuthSession("Sua sessão expirou por inatividade. Faça login novamente.");
    throw new ApiError("Sua sessão expirou por inatividade. Faça login novamente.", 401);
  }

  const accessTokenExpiresAt = getTokenExpiry(session.accessToken);
  const shouldRefresh =
    forceRefresh ||
    (accessTokenExpiresAt !== null &&
      accessTokenExpiresAt - now <= ACCESS_TOKEN_REFRESH_WINDOW_MS);

  if (shouldRefresh) {
    return refreshAccessToken({
      ...session,
      lastActivityAt: now,
    });
  }

  if (now - lastActivityAt >= AUTH_ACTIVITY_THROTTLE_MS) {
    const activeSession = {
      ...session,
      lastActivityAt: now,
    };
    writeAuthSessionCookie(activeSession);
    return activeSession;
  }

  return session;
}

async function withAuthenticatedRequest<TResponse>(
  request: (accessToken: string | null) => Promise<TResponse>,
) {
  const session = await getActiveSession();

  try {
    return await request(session?.accessToken ?? null);
  } catch (cause) {
    if (!(cause instanceof ApiError) || cause.status !== 401 || !session) {
      throw cause;
    }

    const refreshedSession = await getActiveSession(true);
    try {
      return await request(refreshedSession?.accessToken ?? null);
    } catch (retryCause) {
      if (retryCause instanceof ApiError && retryCause.status === 401) {
        expireAuthSession();
        throw new ApiError(
          "Sua sessão expirou. Faça login novamente.",
          401,
          retryCause.details,
        );
      }
      throw retryCause;
    }
  }
}

export function authenticatedRequestJson<TResponse>(
  path: string,
  init: ApiRequestInit = {},
) {
  return withAuthenticatedRequest((accessToken) =>
    requestJson<TResponse>(path, {
      ...init,
      accessToken,
    }),
  );
}

export function authenticatedRequestMultipart<TResponse>(
  path: string,
  body: FormData,
  feedback: ApiRequestFeedback = {},
) {
  return withAuthenticatedRequest((accessToken) =>
    requestJson<TResponse>(path, {
      method: "POST",
      body,
      contentType: null,
      accessToken,
      ...feedback,
    }),
  );
}

export async function authenticatedRequestBlob(
  path: string,
  init: ApiRequestInit = {},
) {
  return withAuthenticatedRequest(async (accessToken) => {
    const {
      operationKey,
      operationalAlertOverride,
      operationalAlertAction,
      suppressOperationalAlert,
      treatTechnicalFailuresAsOperational,
      headers,
      ...requestInit
    } = init;
    const feedback = {
      operationKey,
      operationalAlertOverride,
      operationalAlertAction,
      suppressOperationalAlert,
      treatTechnicalFailuresAsOperational,
    };
    const requestHeaders = new Headers(headers);
    if (accessToken) {
      requestHeaders.set("Authorization", `Bearer ${accessToken}`);
    }

    const response = await fetchApi(
      path,
      {
        ...requestInit,
        headers: requestHeaders,
      },
      feedback,
    );

    if (!response.ok) {
      const details = (await parseResponseBody(response).catch(
        () => null,
      )) as ApiErrorDetails;
      const apiError = new ApiError(
        extractErrorMessage(details),
        response.status,
        details,
      );
      publishClassifiedOperationalFailure(apiError, feedback);
      throw apiError;
    }

    let blob: Blob;
    try {
      blob = await response.blob();
    } catch {
      const parsingError = createResponseParsingError();
      publishClassifiedOperationalFailure(
        parsingError,
        feedbackForParsingFailure(feedback, requestInit.method),
      );
      throw parsingError;
    }

    if (operationKey) {
      clearOperationalAlertsForOperation(operationKey);
    }

    return blob;
  });
}
